import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components';
import { createStartup, getMyStartup } from '../../services/startup-service';

const INDUSTRIES = ['Tech', 'Healthcare', 'Education', 'Finance', 'Other'];
const STAGES = [
  { value: 'idea',     label: 'Idea' },
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed',     label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
];
const COFOUNDER_ROLES = ['CTO', 'COO', 'CFO', 'CMO', 'CPO', 'Other'];

interface CoFounder {
  name: string;
  email: string;
  role: string;
  isFocalTrainee: boolean;
}

const Apply = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [coFounders, setCoFounders] = useState<CoFounder[]>([]);
  const [focalTrainee, setFocalTrainee] = useState<'self' | number>('self');
  const [form, setForm] = useState({
    name: '',
    tagline: '',
    industry: 'Tech',
    stage: 'idea',
    description: '',
    website: '',
    teamSize: 1,
    foundingYear: new Date().getFullYear(),
    problemStatement: '',
    solution: '',
    targetMarket: '',
    revenueModel: '',
    traction: '',
  });

  useEffect(() => {
    getMyStartup()
      .then(startup => {
        if (startup) navigate('/app/startup', { replace: true });
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [navigate]);

  const set = (field: string, value: any) => setForm(prev => ({ ...prev, [field]: value }));

  const addCoFounder = () => {
    setCoFounders(prev => [...prev, { name: '', email: '', role: 'CTO', isFocalTrainee: false }]);
  };

  const updateCoFounder = (index: number, field: keyof CoFounder, value: string) => {
    setCoFounders(prev => prev.map((cf, i) => (i === index ? { ...cf, [field]: value } : cf)));
  };

  const removeCoFounder = (index: number) => {
    setCoFounders(prev => prev.filter((_, i) => i !== index));
    if (focalTrainee === index) setFocalTrainee('self');
    else if (typeof focalTrainee === 'number' && focalTrainee > index) setFocalTrainee(focalTrainee - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Startup name is required.'); return; }
    setIsLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        coFounders: coFounders.map((cf, i) => ({
          ...cf,
          isFocalTrainee: focalTrainee === i,
        })),
        focalTrainee: focalTrainee === 'self' ? 'self' : coFounders[focalTrainee as number]?.email,
      };
      await createStartup(payload);
      navigate('/app/startup');
    } catch (err: any) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  if (checking) {
    return <div className="section-gap"><p className="text-muted">Loading...</p></div>;
  }

  return (
    <div className="section-gap">
      <div>
        <h1 className="text-3xl font-bold text-text">Apply to Program</h1>
        <p className="text-muted mt-1">Tell us about your startup</p>
      </div>

      {error && (
        <div className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-2">Startup Name *</label>
            <input
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Acme Corp"
              required
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Tagline</label>
            <input
              value={form.tagline}
              onChange={e => set('tagline', e.target.value)}
              placeholder="One-line description"
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Industry *</label>
            <select
              value={form.industry}
              onChange={e => set('industry', e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none"
            >
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Stage *</label>
            <select
              value={form.stage}
              onChange={e => set('stage', e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none"
            >
              {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Website</label>
            <input
              value={form.website}
              onChange={e => set('website', e.target.value)}
              placeholder="https://..."
              type="url"
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Team Size</label>
            <input
              value={form.teamSize}
              onChange={e => set('teamSize', parseInt(e.target.value, 10) || 1)}
              type="number"
              min={1}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-2">Founding Year</label>
            <input
              value={form.foundingYear}
              onChange={e => set('foundingYear', parseInt(e.target.value, 10) || new Date().getFullYear())}
              type="number"
              min={2000}
              max={new Date().getFullYear()}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text text-sm focus:outline-none"
            />
          </div>
        </div>

        {/* Long form fields */}
        {[
          { field: 'description',      label: 'Description' },
          { field: 'problemStatement', label: 'Problem Statement' },
          { field: 'solution',         label: 'Solution' },
          { field: 'targetMarket',     label: 'Target Market' },
          { field: 'revenueModel',     label: 'Revenue Model' },
          { field: 'traction',         label: 'Traction' },
        ].map(({ field, label }) => (
          <div key={field}>
            <label className="block text-sm font-medium text-text mb-2">{label}</label>
            <textarea
              value={(form as any)[field]}
              onChange={e => set(field, e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none resize-none focus:border-chart/50"
            />
          </div>
        ))}

        {/* Co-Founders Section */}
        <div className="border-t border-line pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-text">Co-Founders</h2>
              <p className="text-muted text-sm mt-1">Add co-founders to your application. You (the applicant) are automatically included.</p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={addCoFounder}>
              + Add Co-Founder
            </Button>
          </div>

          {/* Focal Trainee: Self */}
          <div className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-line mb-3">
            <input
              type="radio"
              name="focalTrainee"
              checked={focalTrainee === 'self'}
              onChange={() => setFocalTrainee('self')}
              className="accent-chart"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-text">You (Applicant)</span>
              <span className="ml-2 text-xs bg-chart/10 text-chart px-2 py-0.5 rounded">Founder</span>
              {focalTrainee === 'self' && (
                <span className="ml-2 text-xs bg-accent-soft text-chart px-2 py-0.5 rounded">Focal Trainee</span>
              )}
            </div>
          </div>

          {/* Co-Founder Rows */}
          {coFounders.map((cf, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-surface rounded-lg border border-line mb-3">
              <input
                type="radio"
                name="focalTrainee"
                checked={focalTrainee === index}
                onChange={() => setFocalTrainee(index)}
                className="accent-chart mt-2.5"
                title="Designate as focal trainee"
              />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Name</label>
                  <input
                    value={cf.name}
                    onChange={e => updateCoFounder(index, 'name', e.target.value)}
                    placeholder="Full name"
                    className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Email</label>
                  <input
                    value={cf.email}
                    onChange={e => updateCoFounder(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                    type="email"
                    className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-text placeholder:text-muted text-sm focus:outline-none focus:border-chart/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Role</label>
                  <select
                    value={cf.role}
                    onChange={e => updateCoFounder(index, 'role', e.target.value)}
                    className="w-full px-3 py-2 bg-bg border border-line rounded-lg text-text text-sm focus:outline-none"
                  >
                    {COFOUNDER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeCoFounder(index)}
                className="mt-2 text-muted hover:text-danger transition-colors"
                title="Remove co-founder"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {focalTrainee === index && (
                <span className="text-xs bg-accent-soft text-chart px-2 py-0.5 rounded mt-2.5 flex-shrink-0">Focal Trainee</span>
              )}
            </div>
          ))}

          <p className="text-xs text-muted mt-2">
            The focal trainee is the primary participant in all program activities.
            Select the radio button next to a co-founder to designate them instead of yourself.
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" variant="primary" size="lg" disabled={isLoading}>
            {isLoading ? 'Submitting…' : 'Submit Application'}
          </Button>
          <Button type="button" variant="ghost" size="lg" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Apply;
